// src/components/CursosExistentes.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Paper, TextField, Button, Grid } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

// --- helpers ---
const fmtFecha = (v) => (v ? new Date(v).toLocaleDateString("es-ES") : "-");

const toNumOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Normalizador: admite snake/camel y alias frecuentes
const normCurso = (c) => ({
  id: c?.id ?? c?.codigo,
  codigo: c?.codigo ?? null,
  nombre: c?.nombre ?? "",
  fechaDesde: c?.fecha_desde ?? c?.fechaDesde ?? "",
  fechaHasta: c?.fecha_hasta ?? c?.fechaHasta ?? "",
  lugar: c?.lugar ?? "",
  importe: toNumOrNull(c?.importe),
  numeroAlumnos: toNumOrNull(
    c?.numero_alumnos ?? c?.numeroAlumnos ?? c?.num_alumnos ?? c?.alumnos ?? c?.inscritos
  ),
  topeAlumnos: toNumOrNull(
    c?.tope_alumnos ?? c?.topeAlumnos ?? c?.plazas_max ?? c?.cupo ?? c?.aforo
  ),
  horas: toNumOrNull(c?.horas),
  observaciones: c?.observaciones ?? "",
  // maestro como id (o maestro.id si viene expandido)
  maestro: c?.maestro_id ?? c?.maestro ?? c?.maestro?.id ?? null,
  // puede venir 1/0 o true/false
  disponible: c?.disponible ?? 0,
});

// Cols: leemos directamente de row (sin valueFormatter/valueGetter)
const makeColumns = (maestros) => [
  { field: "nombre", headerName: "Nombre del curso", flex: 1 },
  {
    field: "fechas",
    headerName: "Fechas",
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const fd = fmtFecha(params && params.row ? params.row.fechaDesde : null);
      const fh = fmtFecha(params && params.row ? params.row.fechaHasta : null);
      return (
        <div style={{ lineHeight: 1.1 }}>
          <div>{fd}</div>
          <div>→ {fh}</div>
        </div>
      );
    },
  },
  {
    field: "numeroAlumnos",
    headerName: "Nº alumnos",
    flex: 0.35,
    type: "number",
    renderCell: (p) => (p && p.row && p.row.numeroAlumnos != null ? p.row.numeroAlumnos : "-"),
  },
  {
    field: "topeAlumnos",
    headerName: "Tope",
    flex: 0.35,
    type: "number",
    renderCell: (p) => (p && p.row && p.row.topeAlumnos != null ? p.row.topeAlumnos : "-"),
  },
  {
    field: "maestro",
    headerName: "Maestro",
    flex: 0.8,
    renderCell: (p) => {
      const id = p && p.row ? p.row.maestro : null;
      if (id == null) return "-";
      const m = maestros.find((x) => String(x.id) === String(id));
      if (!m) return String(id);
      const ap1 = m.apellido_1 || "";
      const ap2 = m.apellido_2 || "";
      return [m.nombre, ap1, ap2].filter(Boolean).join(" ");
    },
  },
];

export default function CursosExistentes() {
  const [cursos, setCursos] = useState([]);
  const [maestros, setMaestros] = useState([]);
  const [nombre, setNombre] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const buscarCursos = async () => {
    const params = {};
    if (nombre) params.nombre = nombre;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;

    try {
      const { data } = await axios.get(`${API}/api/cursos/buscar`, { params });
      const norm = (Array.isArray(data) ? data : []).map(normCurso);
      // Solo disponibles (1/true)
      const disponibles = norm.filter(
        (c) => Number(c.disponible) === 1 || c.disponible === true
      );
      setCursos(disponibles);
    } catch (e) {
      console.error("Error al buscar cursos:", e);
    }
  };

  useEffect(() => {
    buscarCursos();
    // Cargar maestros para poder mostrar su nombre
    axios
      .get(`${API}/api/maestros`)
      .then((res) => setMaestros(res.data || []))
      .catch((e) => console.error("No se pudieron cargar maestros:", e));
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Cursos disponibles
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar por nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={buscarCursos}
              sx={{ height: "100%" }}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={cursos}
            columns={makeColumns(maestros)}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            disableColumnMenu
            autoHeight
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          />
        </Box>
      </Paper>
    </Box>
  );
}
